// URL test hooks for screenshots, benchmarks and fallback checks. All harmless in production: they only
// change how the world is drawn and the bots-only world behind the lobby, never a game.
//   ?gl=webgpu|webgl2|2d   force a renderer path      ?q=high|medium|low   force a quality tier
//   ?seed=N                seed the lobby's bot world  ?shot=1              hide the panels (screenshots)
//   ?perf=1                show fps / backend / tier   ?cam=x,y,z           point the lobby camera
//   ?fx=1                  the lobby world shows power-ups, rings and an event (for effect shots)
//   ?clip=1                video capture (scripts/clips): a solo game's world is seeded from ?seed too, and a script
//                          can steer it through window.__cgClip.pilot instead of the mouse (deterministic replays)
import { WORLD } from '../shared/config';
import { isTierId, type TierId } from './render3d/quality';

export interface Hooks {
  gl: 'webgpu' | 'webgl2' | '2d' | null;
  tier: TierId | null;
  seed: number | null;
  shot: boolean;
  perf: boolean;
  cam: { x: number; y: number; z: number } | null;
  fx: boolean;
  clip: boolean;
}

export function readHooks(search: string): Hooks {
  const q = new URLSearchParams(search);
  const gl = q.get('gl'), tier = q.get('q'), seed = Number(q.get('seed') ?? NaN);
  const cam = (q.get('cam') ?? '').split(',').map(Number);
  const camOk = cam.length === 3 && cam.every(Number.isFinite) && cam[2] > 0 && cam[2] <= 4
    && Math.abs(cam[0] - WORLD / 2) <= WORLD && Math.abs(cam[1] - WORLD / 2) <= WORLD;
  return {
    gl: gl === 'webgpu' || gl === 'webgl2' || gl === '2d' ? gl : null,
    tier: isTierId(tier) ? tier : null,
    seed: Number.isInteger(seed) ? seed : null,
    shot: q.get('shot') === '1',
    perf: q.get('perf') === '1',
    cam: camOk ? { x: cam[0], y: cam[1], z: cam[2] } : null,
    fx: q.get('fx') === '1',
    clip: q.get('clip') === '1',
  };
}

// mulberry32: a small repeatable stream for the seeded lobby world
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
